import { BusinessRuleResult, UserProfile } from '../types';
import { AdminService } from '@/core/services/AdminService';
import { debugLogger } from '@/utils/debugLogger';

export type AppRole = 'admin' | 'moderator' | 'user';

export class UserRoleRules {
  /**
   * Verifica se admin/moderador pode mudar role de um usuário
   */
  static async canChangeUserRole(
    actor: UserProfile,
    targetUser: UserProfile,
    newRole: AppRole
  ): Promise<BusinessRuleResult> {
    debugLogger.debug('[UserRoleRules]', 'Checking canChangeUserRole', { 
      actorId: actor.id, 
      targetId: targetUser.id, 
      newRole 
    });

    // Regra 1: Apenas admins podem mudar roles
    const isAdmin = await AdminService.isAdmin(actor.id);
    if (!isAdmin) {
      return {
        allowed: false,
        reason: 'Apenas administradores podem gerenciar roles de usuários',
        metadata: { rule: 'admin_only' }
      };
    }

    // Regra 2: Não pode remover próprio admin
    if (actor.id === targetUser.id && newRole !== 'admin') {
      return {
        allowed: false,
        reason: 'Você não pode remover seu próprio status de administrador',
        metadata: { rule: 'self_protection' }
      };
    }

    // Regra 3: Validar que role é válida
    if (!['admin', 'moderator', 'user'].includes(newRole)) {
      return {
        allowed: false,
        reason: 'Role inválida. Opções válidas: admin, moderator, user',
        metadata: { rule: 'valid_role', invalidRole: newRole }
      };
    }

    debugLogger.debug('[UserRoleRules]', 'Role change authorized', {
      actorId: actor.id,
      targetId: targetUser.id,
      newRole
    });

    return {
      allowed: true,
      reason: 'Mudança de role autorizada',
      metadata: { rule: 'authorized' }
    };
  }

  /**
   * Verifica se admin/moderador pode ocultar/mostrar usuário
   */
  static async canToggleUserVisibility(
    actor: UserProfile,
    targetUser: UserProfile
  ): Promise<BusinessRuleResult> {
    debugLogger.debug('[UserRoleRules]', 'Checking canToggleUserVisibility', {
      actorId: actor.id,
      targetId: targetUser.id
    });

    // Verificar se tem acesso ao painel admin (admin ou moderator)
    const canAccessPanel = await AdminService.canAccessAdminPanel(actor.id);
    
    if (!canAccessPanel) {
      return {
        allowed: false,
        reason: 'Sem permissão para gerenciar visibilidade de usuários',
        metadata: { rule: 'no_panel_access' }
      };
    }

    // Obter roles dos usuários em batch (2 chamadas ao invés de 4)
    const [actorRoles, targetRoles] = await Promise.all([
      AdminService.getUserRoles(actor.id),
      AdminService.getUserRoles(targetUser.id)
    ]);
    
    const actorIsAdmin = actorRoles.isAdmin;
    const actorIsModerator = actorRoles.isModerator;
    const targetIsAdmin = targetRoles.isAdmin;
    const targetIsModerator = targetRoles.isModerator;

    // Regra: Moderadores não podem ocultar admins ou outros moderadores
    if (actorIsModerator && !actorIsAdmin && (targetIsAdmin || targetIsModerator)) {
      return {
        allowed: false,
        reason: 'Moderadores não podem ocultar administradores ou outros moderadores',
        metadata: { 
          rule: 'moderator_limitation',
          targetIsAdmin,
          targetIsModerator
        }
      };
    }

    // Regra: Não pode ocultar a si mesmo
    if (actor.id === targetUser.id) {
      return {
        allowed: false,
        reason: 'Você não pode ocultar seu próprio perfil',
        metadata: { rule: 'self_hide_prevention' }
      };
    }

    debugLogger.debug('[UserRoleRules]', 'Visibility toggle authorized', {
      actorId: actor.id,
      targetId: targetUser.id
    });

    return {
      allowed: true,
      reason: 'Ação de visibilidade autorizada',
      metadata: { rule: 'authorized' }
    };
  }

  /**
   * Verifica se ator pode executar ações administrativas sobre usuário
   */
  static async canPerformAdminAction(
    actor: UserProfile,
    targetUser: UserProfile,
    action: string
  ): Promise<BusinessRuleResult> {
    debugLogger.debug('[UserRoleRules]', 'Checking canPerformAdminAction', {
      actorId: actor.id,
      targetId: targetUser.id,
      action
    });

    // Verificar acesso ao painel
    const canAccessPanel = await AdminService.canAccessAdminPanel(actor.id);
    
    if (!canAccessPanel) {
      return {
        allowed: false,
        reason: 'Sem permissão para executar ações administrativas',
        metadata: { rule: 'no_panel_access' }
      };
    }

    // Obter roles dos usuários em batch (2 chamadas ao invés de 4)
    const [actorRoles, targetRoles] = await Promise.all([
      AdminService.getUserRoles(actor.id),
      AdminService.getUserRoles(targetUser.id)
    ]);
    
    const actorIsAdmin = actorRoles.isAdmin;
    const actorIsModerator = actorRoles.isModerator;
    const targetIsAdmin = targetRoles.isAdmin;
    const targetIsModerator = targetRoles.isModerator;

    // Ações restritas apenas a admins
    const adminOnlyActions = ['reset-account', 'suspend', 'manage-role'];
    
    if (adminOnlyActions.includes(action) && !actorIsAdmin) {
      return {
        allowed: false,
        reason: 'Apenas administradores podem executar esta ação',
        metadata: { rule: 'admin_only_action', action }
      };
    }

    // Moderadores não podem realizar ações em admins ou outros moderadores
    if (actorIsModerator && !actorIsAdmin && (targetIsAdmin || targetIsModerator)) {
      return {
        allowed: false,
        reason: 'Moderadores não podem executar ações em administradores ou outros moderadores',
        metadata: { 
          rule: 'moderator_limitation',
          action,
          targetIsAdmin,
          targetIsModerator
        }
      };
    }

    debugLogger.debug('[UserRoleRules]', 'Admin action authorized', {
      actorId: actor.id,
      targetId: targetUser.id,
      action
    });

    return {
      allowed: true,
      reason: 'Ação administrativa autorizada',
      metadata: { rule: 'authorized', action }
    };
  }
}
